import React, { useState, useEffect } from 'react';
import { useSchool } from '../context/SchoolContext';
import { TrendingDown, X, Tag, CreditCard, Calendar, FileText, ChevronDown, AlertCircle } from 'lucide-react';
import './FinancialModal.css';

const AddExpenseModal = ({ isOpen, onClose, expenseToEdit = null }) => {
    const { addExpense, updateExpense, expenseCategories = [], paymentMethods = [], branches = [] } = useSchool();
    const isEditing = !!expenseToEdit;

    const [expenseData, setExpenseData] = useState({
        category: '',
        amount: '',
        method: 'Cash',
        description: '',
        date: new Date().toISOString().split('T')[0],
        branch_id: ''
    });

    useEffect(() => {
        if (isOpen) {
            if (expenseToEdit) {
                setExpenseData({
                    category: expenseToEdit.category || '',
                    amount: expenseToEdit.amount || '',
                    method: expenseToEdit.method || 'Cash',
                    description: expenseToEdit.description || '',
                    date: (expenseToEdit.expense_date || expenseToEdit.date || '').split('T')[0] || new Date().toISOString().split('T')[0],
                    branch_id: expenseToEdit.branch_id || ''
                });
            } else {
                setExpenseData({
                    category: expenseCategories[0] || '',
                    amount: '',
                    method: paymentMethods[0] || 'Cash',
                    description: '',
                    date: new Date().toISOString().split('T')[0],
                    branch_id: ''
                });
            }
        }
    }, [isOpen, expenseToEdit, expenseCategories, paymentMethods]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setExpenseData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const finalData = {
            ...expenseData,
            amount: parseFloat(expenseData.amount)
        };

        if (isEditing) {
            updateExpense(expenseToEdit.id, finalData);
        } else {
            addExpense(finalData);
        }
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="f-modal-overlay">
            <div className="f-modal-content">
                <div className="f-modal-header">
                    <div className="f-header-info">
                        <div className="f-icon-box expense">
                            <TrendingDown size={24} />
                        </div>
                        <div>
                            <h3 className="f-modal-title">{isEditing ? 'Edit Expense' : 'Record Expense'}</h3>
                            <p className="text-secondary text-sm">{isEditing ? 'Update the details of this expense' : 'Add a outgoing payment / payout'}</p>
                        </div>
                    </div>
                    <button className="f-close-btn" onClick={onClose}><X size={20} /></button>
                </div>

                <form onSubmit={handleSubmit} className="f-modal-form expense">
                    <div className="f-form-grid">
                        <div className="f-field-group f-field-full">
                            <label className="f-label">
                                <Tag size={14} /> EXPENSE CATEGORY
                            </label>
                            <div className="f-input-wrapper">
                                <Tag className="f-input-icon" size={18} />
                                <select
                                    name="category"
                                    className="f-input f-select"
                                    required
                                    value={expenseData.category}
                                    onChange={handleInputChange}
                                >
                                    <option value="">Select Category...</option>
                                    {expenseCategories.map(c => (
                                        <option key={c} value={c}>{c}</option>
                                    ))}
                                </select>
                                <ChevronDown className="f-chevron" size={18} />
                            </div>
                        </div>

                        <div className="f-field-group">
                            <label className="f-label">
                                <AlertCircle size={14} /> AMOUNT OUT ($)
                            </label>
                            <div className="f-input-wrapper">
                                <CreditCard className="f-input-icon" size={18} />
                                <input
                                    type="number"
                                    name="amount"
                                    className="f-input f-amount-p"
                                    placeholder="0.00"
                                    step="0.01"
                                    required
                                    value={expenseData.amount}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>

                        <div className="f-field-group">
                            <label className="f-label">
                                <CreditCard size={14} /> PAYMENT METHOD
                            </label>
                            <div className="f-input-wrapper">
                                <CreditCard className="f-input-icon" size={18} />
                                <select
                                    name="method"
                                    className="f-input f-select"
                                    required
                                    value={expenseData.method}
                                    onChange={handleInputChange}
                                >
                                    {paymentMethods.map(m => (
                                        <option key={m} value={m}>{m}</option>
                                    ))}
                                </select>
                                <ChevronDown className="f-chevron" size={18} />
                            </div>
                        </div>

                        <div className="f-field-group f-field-full">
                            <label className="f-label">
                                <Calendar size={14} /> DATE
                            </label>
                            <div className="f-input-wrapper">
                                <Calendar className="f-input-icon" size={18} />
                                <input
                                    type="date"
                                    name="date"
                                    className="f-input"
                                    required
                                    value={expenseData.date}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>

                        <div className="f-field-group f-field-full">
                            <label className="f-label">
                                <Tag size={14} /> BRANCH
                            </label>
                            <div className="f-input-wrapper">
                                <Tag className="f-input-icon" size={18} />
                                <select
                                    name="branch_id"
                                    className="f-input f-select"
                                    value={expenseData.branch_id}
                                    onChange={handleInputChange}
                                >
                                    <option value="">-- No Branch Assiged / Global --</option>
                                    {branches.map(b => (
                                        <option key={b.id} value={b.id}>{b.branch_name}</option>
                                    ))}
                                </select>
                                <ChevronDown className="f-chevron" size={18} />
                            </div>
                        </div>

                        <div className="f-field-group f-field-full">
                            <label className="f-label">
                                <FileText size={14} /> DESCRIPTION / REF
                            </label>
                            <div className="f-input-wrapper">
                                <FileText className="f-input-icon" style={{ top: '1rem' }} size={18} />
                                <textarea
                                    name="description"
                                    className="f-input f-textarea"
                                    placeholder="e.g. Fuel for Hilux, Water bill for Jan..."
                                    rows="3"
                                    value={expenseData.description}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="f-modal-footer">
                        <button type="button" className="f-btn f-btn-secondary" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="f-btn f-btn-danger">
                            <TrendingDown size={18} /> {isEditing ? 'Update Expense' : 'Record Payout'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddExpenseModal;
